// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Public Website Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Collaborate from './pages/Collaborate';
import RequestDemo from './pages/RequestDemo';
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import CookiePolicy from "./pages/CookiePolicy";

// Components
// We remove Header and Footer from here because they are now in PublicLayout
import ModernAiModal from './components/AiModal';
import POPUP from './components/POPUP';
import ChatComponent from './components/MainComponents';

// NEW: Import the layout component
import PublicLayout from './components/PublicLayout'; 

// Dashboard and Auth Contexts & Components
import { AuthProvider } from './dashboard/context/AuthProvider';
import { ChatProvider } from './dashboard/context/ChatProvider';
import { LoginPage } from './dashboard/pages/LoginPage';
import { DashboardPage } from './dashboard/pages/DashboardPage';
import { ProtectedRoute } from './dashboard/components/ProtectedRoute';

// Superadmin Routes
import { SuperadminDashboardPage } from './dashboard/pages/SuperadminDashboardPage';
import { SuperadminRoute } from './dashboard/components/SuperadminRoute';

function App() {
  // State for AI Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // State to manage the visibility of the cookie banner
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    // Session ID logic for the AI chat
    let storedSessionId = sessionStorage.getItem('sessionId');
    if (!storedSessionId) {
      storedSessionId = `user_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('sessionId', storedSessionId);
    }
    setSessionId(storedSessionId);

    // Logic to check for cookie consent on app load
    const consent = localStorage.getItem('cookie_consent');
    if (consent === null) {
      // If no consent has been given, show the banner
      setShowCookieBanner(true);
    }
  }, []);

  const openAiModal = () => setIsModalOpen(true);
  const closeAiModal = () => setIsModalOpen(false);

  const handleAcceptCookies = () => {
    console.log("Cookies Accepted by user.");
    localStorage.setItem('cookie_consent', 'accepted');
    setShowCookieBanner(false);
  };

  const handleDeclineCookies = () => {
    console.log("Cookies Declined by user.");
    localStorage.setItem('cookie_consent', 'declined');
    setShowCookieBanner(false);
  };

  return (
    <AuthProvider>
      <Router>
        {/* 
          The Header and Footer have been removed from here.
          The routing logic below will now handle when they appear.
        */}

        <Routes>
          {/* Routes WITHOUT Header and Footer */}
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/superadmin"
            element={
              <SuperadminRoute>
                <SuperadminDashboardPage />
              </SuperadminRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ChatProvider>
                  <DashboardPage />
                </ChatProvider>
              </ProtectedRoute>
            }
          />

          {/* Public routes that WILL have the Header and Footer */}
          {/* We wrap them in the PublicLayout component */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/collaborate" element={<Collaborate />} />
            <Route path="/request-demo" element={<RequestDemo />} />
            <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            <Route path="/TermsConditions" element={<TermsConditions />} />
            <Route path="/CookiePolicy" element={<CookiePolicy />} />
          </Route>
        </Routes>

        {/* Global components like Modals and Popups can stay here */}
        {sessionId && (
          <>
            <button
              onClick={openAiModal}
              aria-label="Open AI Chat"
              title="Chat with AI Assistant"
              className="fixed bottom-8 right-8 flex h-14 w-14 items-center justify-center rounded-full border-none bg-transparent cursor-pointer animate-pulse-glow transition-transform duration-300 hover:scale-110 hover:animate-none active:scale-100 z-50"
            >
              <img src="/images/voidwhite.png" alt="Void AI Chat Icon" />
            </button>

            <ModernAiModal
              isOpen={isModalOpen}
              onClose={closeAiModal}
              currentUserId={sessionId}
              lang="en"
            />
          </>
        )}

        {showCookieBanner && (
          <POPUP
            onAccept={handleAcceptCookies}
            onDecline={handleDeclineCookies}
          />
        )}

        {/* This component can also stay as it's a global overlay */}
        <ChatComponent />
      </Router>
    </AuthProvider>
  );
}

export default App;